/**
 * 背景音乐库管理页面
 * 上传、预览、删除背景音乐，用于成长视频制作
 */
import { useState, useEffect } from 'react'
import { Card, Button, Space, message, Upload, Modal, Table, Input } from 'antd'

// API base: /cms/api/

// 音乐分类标签
const CATEGORY_TAGS = {
  warm: { label: '温暖治愈', color: '#fa8c16' },
  joyful: { label: '欢快活泼', color: '#f5222d' },
  gentle: { label: '轻柔舒缓', color: '#52c41a' },
  inspiring: { label: '励志向上', color: '#1890ff' },
  peaceful: { label: '宁静温馨', color: '#722ed1' },
  childhood: { label: '童年轻唱', color: '#eb2f96' },
  happiness: { label: '幸福时光', color: '#faad14' },
  nature: { label: '自然之声', color: '#13c2c2' },
  adventure: { label: '冒险旅程', color: '#fa541c' },
  friendship: { label: '友谊万岁', color: '#2f54eb' },
  family: { label: '亲情暖阳', color: '#a0d911' },
  harvest: { label: '丰收喜悦', color: '#fadb14' },
  dream: { label: '梦幻童年', color: '#722ed1' },
  classic: { label: '经典名曲', color: '#13c2c2' },
  sunshine: { label: '阳光少年', color: '#fa8c16' },
  forever: { label: '永恒生命', color: '#eb2f96' },
  nostalgia: { label: '童年情景', color: '#a0d911' },
  lake: { label: '泛舟湖上', color: '#1890ff' },
  world: { label: '小小世界', color: '#52c41a' },
  star: { label: '小星星', color: '#faad14' },
  dragon: { label: '小龙人', color: '#f5222d' },
  ready: { label: '准备好了吗', color: '#13c2c2' },
  heart: { label: '只为你心', color: '#eb2f96' },
  morning: { label: '晨光熹微', color: '#fa8c16' },
}

export default function MusicLibraryPage() {
  const [musicList, setMusicList] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [previewMusic, setPreviewMusic] = useState(null)
  const [audioRef, setAudioRef] = useState(null)
  const [playing, setPlaying] = useState(null)
  const [editingMusic, setEditingMusic] = useState(null)
  const [editName, setEditName] = useState('')
  const [editCategory, setEditCategory] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editModalVisible, setEditModalVisible] = useState(false)
  // 打开编辑弹窗
  const handleEdit = (record) => {
    setEditingMusic(record)
    setEditName(record.name)
    setEditCategory(record.category || '')
    setEditDesc(record.description || '')
    setEditModalVisible(true)
  }
  
  // 保存编辑
  const handleSaveEdit = async () => {
    if (!editingMusic) return
    try {
      const res = await fetch(`/cms/api/admin/music/${editingMusic.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName, category: editCategory, description: editDesc })
      })
      const json = await res.json()
      if (json.success) {
        message.success('更新成功')
        setEditModalVisible(false)
        loadMusicList()
      } else {
        message.error(json.error || '更新失败')
      }
    } catch (err) {
      message.error('更新失败: ' + err.message)
    }
  }
  
  // 加载音乐列表
  const loadMusicList = async () => {
    setLoading(true)
    try {
      const res = await fetch(`/cms/api/admin/music/list`)
      const json = await res.json()
      if (json.success) {
        setMusicList(json.data || [])
      } else {
        console.error('获取音乐列表失败:', json.error)
      }
    } catch (err) {
      console.error('加载音乐列表失败:', err)
    }
    setLoading(false)
  }

  useEffect(() => {
    loadMusicList()
  }, [])

  // 过滤音乐
  const getFilteredMusic = () => {
    if (!searchKeyword) return musicList
    const keyword = searchKeyword.toLowerCase()
    return musicList.filter(m => 
      m.name.toLowerCase().includes(keyword) || 
      m.file.toLowerCase().includes(keyword) ||
      (CATEGORY_TAGS[m.category]?.label || '').toLowerCase().includes(keyword)
    )
  }

  // 上传音乐
  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    if (!file.type.includes('audio')) {
      alert('只能上传音频文件（MP3、WAV等）')
      return
    }
    if (file.size > 50 * 1024 * 1024) {
      alert('文件大小不能超过50MB')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', 'music')

      const res = await fetch(`/cms/api/admin/music/upload`, {
        method: 'POST',
        body: formData
      })
      const json = await res.json()
      if (json.success) {
        alert('上传成功')
        loadMusicList()
      } else {
        alert(json.error || '上传失败')
      }
    } catch (err) {
      console.error('上传失败:', err)
      alert('上传失败，请重试')
    }
    setUploading(false)
    e.target.value = ''
  }

  // 删除音乐
  const handleDelete = async (record) => {
    if (!confirm(`确定要删除音乐「${record.name}」吗？`)) return
    
    try {
      const res = await fetch(`/cms/api/admin/music/${record.id}`, {
        method: 'DELETE'
      })
      const json = await res.json()
      if (json.success) {
        alert('删除成功')
        loadMusicList()
      } else {
        alert(json.error || '删除失败')
      }
    } catch (err) {
      alert('删除失败')
    }
  }

  // 播放/暂停
  const handlePlay = (record) => {
    if (playing === record.id) {
      audioRef?.pause()
      setPlaying(null)
    } else {
      if (audioRef) {
        audioRef.src = (record.url || record.file_path || '').replace(/\/storage\//, '/cms/api/ai-videos/proxy/video?path=')
        audioRef.play()
        setPlaying(record.id)
        setPreviewMusic(record)
      }
    }
  }

  const filteredMusic = getFilteredMusic()

  return (
    <div style={{ padding: 24 }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>背景音乐库</h2>
        <p style={{ color: '#666', margin: '8px 0 0', fontSize: 13 }}>
          管理成长视频的背景音乐，支持上传、试听和删除
        </p>
      </div>

      {/* 统计卡片 */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#fff', padding: 16, borderRadius: 8, flex: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 12, color: '#999' }}>音乐总数</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', marginTop: 4 }}>{musicList.length}</div>
        </div>
        <div style={{ background: '#fff', padding: 16, borderRadius: 8, flex: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 12, color: '#999' }}>可选分类</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', marginTop: 4 }}>{Object.keys(CATEGORY_TAGS).length}</div>
        </div>
        <div style={{ background: '#fff', padding: 16, borderRadius: 8, flex: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 12, color: '#999' }}>存储位置</div>
          <div style={{ fontSize: 14, fontWeight: 'bold', marginTop: 4, color: '#1890ff' }}>storage/bgm/</div>
        </div>
      </div>

      {/* 操作栏 */}
      <div style={{ background: '#fff', padding: 16, borderRadius: 8, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <input
              type="text"
              placeholder="搜索音乐..."
              value={searchKeyword}
              onChange={e => setSearchKeyword(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 4, width: 200 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button 
              onClick={loadMusicList}
              style={{ padding: '8px 16px', border: '1px solid #d9d9d9', background: '#fff', borderRadius: 4, cursor: 'pointer' }}
            >
              🔄 刷新
            </button>
            <label style={{ padding: '8px 16px', background: '#1890ff', color: '#fff', borderRadius: 4, cursor: 'pointer', display: 'inline-block' }}>
              {uploading ? '上传中...' : '📤 上传音乐'}
              <input type="file" accept=".mp3,.wav,.ogg,.m4a,.flac" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
            </label>
          </div>
        </div>
      </div>

      {/* 音乐列表 */}
      <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ padding: '16px 16px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px 80px 80px 120px', gap: 12, fontSize: 12, color: '#999', paddingBottom: 12 }}>
            <div>音乐名称</div>
            <div>文件名</div>
            <div>分类</div>
            <div>大小</div>
            <div>时长</div>
            <div>操作</div>
          </div>
        </div>
        
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>加载中...</div>
        ) : filteredMusic.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            {searchKeyword ? '没有找到匹配的音乐' : '暂无音乐，请上传'}
          </div>
        ) : (
          filteredMusic.map((music, idx) => (
            <div 
              key={music.id || idx}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: '2fr 1fr 100px 80px 80px 120px', 
                gap: 12, 
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                alignItems: 'center'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>🎵</span>
                <span style={{ fontWeight: 500 }}>{music.name}</span>
              </div>
              <div style={{ color: '#999', fontSize: 12 }}>{music.file}</div>
              <div>
                {CATEGORY_TAGS[music.category] ? (
                  <span style={{ 
                    padding: '2px 8px', 
                    borderRadius: 4, 
                    fontSize: 11,
                    background: CATEGORY_TAGS[music.category].color + '20',
                    color: CATEGORY_TAGS[music.category].color
                  }}>
                    {CATEGORY_TAGS[music.category].label}
                  </span>
                ) : (
                  <span style={{ color: '#999', fontSize: 12 }}>{music.category}</span>
                )}
              </div>
              <div style={{ color: '#999', fontSize: 12 }}>{music.size || '未知'}</div>
              <div style={{ color: '#999', fontSize: 12 }}>{music.duration || '未知'}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button 
                  onClick={() => handlePlay(music)}
                  style={{ 
                    padding: '4px 12px', 
                    border: playing === music.id ? '1px solid #1890ff' : '1px solid #d9d9d9',
                    background: playing === music.id ? '#e6f4ff' : '#fff',
                    color: playing === music.id ? '#1890ff' : '#666',
                    borderRadius: 4, 
                    cursor: 'pointer',
                    fontSize: 12
                  }}
                >
                  {playing === music.id ? '⏸ 暂停' : '▶ 试听'}
                </button>
                <button
                  onClick={() => handleEdit(music)}
                  style={{
                    padding: '4px 12px',
                    border: '1px solid #1890ff',
                    background: '#fff',
                    color: '#1890ff',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 12
                  }}
                >
                  ✏️ 编辑
                </button>
                <button
                  onClick={() => handleDelete(music)}
                  style={{ 
                    padding: '4px 12px', 
                    border: '1px solid #ff4d4f',
                    background: '#fff',
                    color: '#ff4d4f',
                    borderRadius: 4, 
                    cursor: 'pointer',
                    fontSize: 12
                  }}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 播放提示 */}
      {previewMusic && playing && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1890ff',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: 24,
          boxShadow: '0 4px 12px rgba(24,144,255,0.4)',
          zIndex: 1000
        }}>
          🎵 正在播放：{previewMusic.name}
          <button 
            onClick={() => { audioRef?.pause(); setPlaying(null); }}
            style={{ marginLeft: 16, padding: '4px 12px', background: '#fff', color: '#1890ff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            停止
          </button>
        </div>
      )}

      {/* 音频播放器 */}
      <audio ref={setAudioRef} onEnded={() => setPlaying(null)} style={{ display: 'none' }} />

      {/* 编辑弹窗 */}
      <Modal title="编辑音乐" open={editModalVisible} onOk={handleSaveEdit} onCancel={() => setEditModalVisible(false)}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>名称 *</label>
          <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="音乐名称" />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>分类</label>
          <Input value={editCategory} onChange={e => setEditCategory(e.target.value)} placeholder="如：欢快、舒缓" />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4 }}>描述</label>
          <Input.TextArea value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="音乐描述" rows={3} />
        </div>
      </Modal>
    </div>
  )
}
